import { z } from "zod";

/**
 * Media source type
 * - user-upload: Uploaded by user
 * - ai-generated: Generated by AI (e.g., TTS audio)
 * - media-house: Copied from Media House
 */
export const MediaSourceSchema = z.enum(["user-upload", "ai-generated", "media-house"]);

export type MediaSource = z.infer<typeof MediaSourceSchema>;

/**
 * Media usage context type
 * - thumbnail: Used as thumbnail/cover image for projects/lessons/slides (not shared)
 * - slideEditor: Used within slide content (can be shared across slides)
 * - vocabCard: Used for vocabulary card images and audio
 * - audioLibrary: Uploaded into an audio folder for students to listen to directly
 * - composition: A student's photo of their handwritten composition, uploaded for AI correction
 */
export const MediaUsageContextSchema = z.enum([
  "thumbnail",
  "slideEditor",
  "vocabCard",
  "aiTextToSpeech",
  "audioLibrary",
  "composition",
]);

export type MediaUsageContext = z.infer<typeof MediaUsageContextSchema>;

/**
 * Base schema for all media types (Image, Audio, Video)
 * Contains common fields shared across all media
 */
export const BaseMediaSchema = z.object({
  id: z.string(),
  key: z.string(), // S3 key — the signed/bare media URL is computed from this at read time
  filename: z.string(),
  size: z.number(), // File size in bytes
  tags: z.array(z.string()),
  mimeType: z.string(),

  mediaHouseId: z.string().nullable(), // Used to prevent duplicate S3 copies

  usageContext: MediaUsageContextSchema, // Determines how media is referenced and cleaned up

  isTemporary: z.boolean(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export type BaseMedia = z.infer<typeof BaseMediaSchema>;

/**
 * Image schema
 * Extends base media with an optional display (compressed WebP) version key.
 * displayKey is null for images uploaded before the display version feature
 * was introduced — consumers must fall back to `key`.
 */
export const ImageSchema = BaseMediaSchema.extend({
  displayKey: z.string().nullable().optional(),
});

export type Image = z.infer<typeof ImageSchema>;

/**
 * Word boundary schema — per-word timing from TTS synthesis, used to highlight text in sync
 * with audio playback. `offset`/`duration` are milliseconds relative to the audio start.
 */
export const WordBoundarySchema = z.object({
  text: z.string(),
  offset: z.number(),
  duration: z.number(),
});

export type WordBoundary = z.infer<typeof WordBoundarySchema>;

/**
 * Audio schema
 * Extends base media with audio-specific fields
 */
export const AudioSchema = BaseMediaSchema.extend({
  audioText: z.string().nullable(),
  audioTextHash: z.string().nullable(),
  voice: z.string().nullable(),
  /** Azure ticks (100 ns). Recorded so audio reused from the hash cache keeps its duration. */
  durationTicks: z.number().nullable().optional(),
  wordBoundaries: z.array(WordBoundarySchema).nullable().optional(),
});

export type Audio = z.infer<typeof AudioSchema>;

/**
 * Video schema
 * Extends base media with video-specific fields
 */
export const VideoSchema = BaseMediaSchema.extend({
  audioText: z.string().nullable(),
});

export type Video = z.infer<typeof VideoSchema>;
